import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { Pagina, AtualizarRascunhoPagina, RegistroAuditoria } from '@pgcustom/shared-types';

/**
 * Adapter do módulo CMS no frontend — único ponto que conhece o
 * formato das rotas `/api/v1/cms/*` (`apps/api/src/modules/cms/pagina.routes.ts`).
 * Sem `criar`/`excluir`: o conjunto de páginas é fixo (migration `017`).
 */
export class CmsApi {
  constructor(private readonly api: ApiClient) {}

  async listar(): Promise<Resultado<Pagina[]>> {
    try {
      const resposta = await this.api.get<{ paginas: Pagina[] }>('/cms/paginas');
      if (resposta.paginas.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.paginas };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar páginas' };
    }
  }

  async obterPorId(id: string): Promise<Pagina> {
    const resposta = await this.api.get<{ pagina: Pagina }>(`/cms/paginas/${id}`);
    return resposta.pagina;
  }

  async atualizarRascunho(id: string, patch: AtualizarRascunhoPagina): Promise<Pagina> {
    const resposta = await this.api.patch<{ pagina: Pagina }>(`/cms/paginas/${id}/rascunho`, patch);
    return resposta.pagina;
  }

  async publicar(id: string): Promise<Pagina> {
    const resposta = await this.api.post<{ pagina: Pagina }>(`/cms/paginas/${id}/publicar`, {});
    return resposta.pagina;
  }

  async despublicar(id: string): Promise<Pagina> {
    const resposta = await this.api.post<{ pagina: Pagina }>(`/cms/paginas/${id}/despublicar`, {});
    return resposta.pagina;
  }

  async listarVersoes(id: string): Promise<RegistroAuditoria[]> {
    const resposta = await this.api.get<{ versoes: RegistroAuditoria[] }>(`/cms/paginas/${id}/versoes`);
    return resposta.versoes;
  }

  async restaurar(id: string, versaoId: string): Promise<Pagina> {
    const resposta = await this.api.post<{ pagina: Pagina }>(`/cms/paginas/${id}/versoes/${versaoId}/restaurar`, {});
    return resposta.pagina;
  }
}
